import { type DocumentParameters, type FetchClient, type PageParameters } from './http';
import type { Permalink, PermalinkCampaign, PermalinkNotificationRequest, PermalinkPage, PermalinkRequest } from './types/permalinks';
import type { Identifier } from './types/rights';

export const getSessionPermalinks = ({
  client,
  page = '1',
  size = '50',
  sort = 'contentFileName',
  query = undefined,
}: { client: FetchClient } & PageParameters & { sort?: string; query?: string }): Promise<PermalinkPage> => {
  const whereQuery = query !== undefined ? `&q=${query}` : '';
  return client.get(`${client.baseUrl}/permalinks?page=${page}&size=${size}&sort=${sort}${whereQuery}`);
};

export const getPermalinkContentSize = ({ client, permalinkId }: { client: FetchClient; permalinkId: string }): Promise<number> => {
  return client.head(`${client.baseUrl}/permalinks/${permalinkId}`, 'application/json');
};

export const getPermalinkProperties = ({ client, permalinkId }: { client: FetchClient; permalinkId: string }): Promise<Permalink> => {
  return client.get(`${client.baseUrl}/permalinks/${permalinkId}/properties`);
};

export const getPermalink = ({
  client,
  permalinkId,
  range,
  omitAuth = false,
}: {
  client: FetchClient;
  permalinkId: string;
  range?: string;
  omitAuth?: boolean;
}): Promise<Response> => {
  return client.raw(`${client.baseUrl}/permalinks/${permalinkId}`, { accept: '*/*', range, omitAuth });
};

export const getPermalinks = ({
  client,
  workspaceId,
  documentId,
  versionId,
  page = '1',
  size = '20',
  sort,
  mimeType,
}: { client: FetchClient } & DocumentParameters & {
    versionId: string;
    sort?: string;
    mimeType?: string;
  } & PageParameters): Promise<PermalinkPage> => {
  const urlSearchParams = new URLSearchParams({ page, size });
  sort !== undefined && urlSearchParams.set('sort', sort);
  mimeType !== undefined && urlSearchParams.set('mimeType', mimeType);
  return client.get(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/permalinks?$` + urlSearchParams
  );
};

export const createPermalink = ({
  client,
  permalinkRequest,
}: {
  client: FetchClient;
  permalinkRequest: PermalinkRequest;
}): Promise<Permalink> => {
  return client.post(`${client.baseUrl}/permalinks`, permalinkRequest);
};

export const updatePermalink = ({
  client,
  permalinkId,
  expirationDateTime,
  sharedWith,
  maxUses,
}: {
  client: FetchClient;
  permalinkId: string;
  expirationDateTime?: string;
  sharedWith?: Identifier[];
  maxUses?: number;
}): Promise<Permalink> => {
  const body: any = {};
  if (expirationDateTime) {
    body.expirationDateTime = expirationDateTime;
  }
  if (sharedWith) {
    body.sharedWith = sharedWith;
  }
  if (maxUses !== undefined) {
    body.maxUses = maxUses;
  }
  return client.patch(`${client.baseUrl}/permalinks/${permalinkId}`, body);
};

export const notifyPermalink = ({
  client,
  permalinkId,
  permalinkNotification,
}: {
  client: FetchClient;
  permalinkId: string;
  permalinkNotification: PermalinkNotificationRequest;
}): Promise<PermalinkCampaign> => {
  return client.post(`${client.baseUrl}/permalinks/${permalinkId}/notify`, permalinkNotification);
};

export const revokePermalink = ({ client, permalinkId }: { client: FetchClient; permalinkId: string }): Promise<boolean> => {
  return client.del(`${client.baseUrl}/permalinks/${permalinkId}`);
};
